import React, { useState } from 'react'
import { Accordion, AccordionDetails, AccordionSummary, Box, Paper, Typography } from '@mui/material'
import { unstable_useId as useId } from '@mui/material/utils'
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded'
import { advancedColors } from '../theme'
import CardHelp from './CardHelp'

type Props = {
  title: string
  action?: React.ReactNode
  children: React.ReactNode
  scroll?: boolean
  // `display: none` rather than unmounting, so a collapsed panel's own state survives re-expanding.
  bodyHidden?: boolean
  collapsible?: boolean
  onExpandedChange?: (expanded: boolean) => void
  help?: string
}

const SectionCard = ({
  title,
  action,
  children,
  scroll,
  bodyHidden,
  collapsible = false,
  help,
  onExpandedChange,
}: Props) => {
  const [expanded, setExpanded] = useState(false)
  const bodyId = useId()

  if (collapsible) {
    return (
      <Accordion
        disableGutters
        elevation={0}
        expanded={expanded && !bodyHidden}
        onChange={(_event, next) => {
          setExpanded(next)
          onExpandedChange?.(next)
        }}
        // Keep forms and pending work mounted when a card closes.
        TransitionProps={{ timeout: 200, unmountOnExit: false }}
        sx={{ borderRadius: 1, overflow: 'hidden', '&:before': { display: 'none' } }}
      >
        <AccordionSummary
          id={`${bodyId}-header`}
          aria-controls={bodyId}
          aria-label={title}
          expandIcon={<ExpandMoreRoundedIcon sx={{ color: advancedColors.inkMuted }} />}
          sx={{
            minHeight: 0,
            px: 2.5,
            '&.Mui-expanded': { minHeight: 0 },
            '& .MuiAccordionSummary-content': { margin: '10px 0', minWidth: 0, alignItems: 'center' },
            '& .MuiAccordionSummary-content.Mui-expanded': { margin: '10px 0' },
          }}
        >
          <Typography component="h2" variant="h3" sx={{ color: advancedColors.ink }}>
            {title}
          </Typography>
          {help && <CardHelp title={title}>{help}</CardHelp>}
        </AccordionSummary>
        <AccordionDetails
          sx={{
            p: 2.5,
            minHeight: 0,
            borderTop: `1px solid ${advancedColors.line}`,
            ...(scroll ? { overflowY: 'auto' } : null),
          }}
        >
          {action && <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>{action}</Box>}
          {children}
        </AccordionDetails>
      </Accordion>
    )
  }

  return (
    <Paper sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, overflow: 'clip' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 2.5,
          py: 1,
          borderBottom: bodyHidden ? 'none' : `1px solid ${advancedColors.line}`,
        }}
      >
        <Typography component="h2" variant="h3" sx={{ color: advancedColors.ink }}>
          {title}
        </Typography>
        {help && <CardHelp title={title}>{help}</CardHelp>}
        {action}
      </Box>
      <Box
        sx={{
          display: bodyHidden ? 'none' : 'block',
          p: 2.5,
          minHeight: 0,
          ...(scroll ? { overflowY: 'auto' } : null),
        }}
      >
        {children}
      </Box>
    </Paper>
  )
}

export default SectionCard
